import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useEffect, useState} from 'react';
import {Box, styled} from '@mui/material';
import {BgCurve} from '@jobseeker/lotus/assets/images';
import {
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {getRefTagAndOrigin} from '@components/HomeV2/homeCategory';
import HomePageFooter from '@components/HomeV2/homePageFooter';
import {Static} from '@components/HomeV2/static';
import HomePageCompanyIcons from '@components/HomeV2/homePageCompanyJobs';
import dynamic from 'next/dynamic';
import {Loader} from '@jobseeker/lotus/components';
import TalentBridgeBanner from '@components/TalentBridge/TalentBridgeLanding/TalentBridgeBanner';
import {formQueryParams} from '@utils';
import {
  DriveBanner,
  getNewDriveBannerData,
  setCampaignData,
  talentBridgeContants,
} from '../utils';

const Img = styled(Image)(({theme}) => ({
  position: 'absolute',
  left: 0,
  width: '100%',
  top: 0,
  height: 705,
  zIndex: -1,

  [theme.breakpoints.up('lg')]: {
    height: 770, // min-width: 1280px
  },
  [theme.breakpoints.down(1024)]: {
    height: 1023, // max-width: 1023px
  },
  [theme.breakpoints.down(430)]: {
    height: 650, // max-width: 430px
  },
}));

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const CookieBanner = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/CookieBanner'
    ),
  {
    loading: () => <Loader />,
  }
);

type TalentBridgeLandingProps = {
  city: string;
};

const TalentBridgeLanding = ({city}: TalentBridgeLandingProps) => {
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [driveBannerObj, setDriveBannerObj] = useState<DriveBanner | null>(
    null
  );
  const userId = '-1';
  const [url, setUrl] = useState(process.env.NEXT_PUBLIC_LOAD_CONFIG);
  const router = useRouter();

  useEffect(() => {
    setUrl(window.location.href);
    const fetchData = async () => {
      try {
        const obj = await getNewDriveBannerData(city);
        setDriveBannerObj(obj);
        localStorage.setItem(
          'talentBridgeMonthCity',
          JSON.stringify({
            month: obj.month,
            city: city.charAt(0).toUpperCase() + city.slice(1),
          })
        );
      } catch (err) {
        console.error('Error fetching drive banner data:', err);
      }
    };
    fetchData();
  }, [city]);

  const urlObj = new URL(url ?? '');
  const queryParams = Object.fromEntries(urlObj.searchParams.entries());

  if (router?.pathname?.indexOf('/register') != -1) {
    TrackEventUBA('registrationInitialOverlayPageView', 'view', {
      ref: queryParams.ref || '',
      sessionUsername: userId ? userId.toString() : '',
    });
  }

  const handleUbaTracking = (eventName: string, section: string) => {
    TrackEventUBA(eventName, 'click', {
      page: 'jsHomePage',
      section,
      ref: queryParams.ref || '',
      sessionUsername: userId ? userId.toString() : '',
    });
  };

  const handleLogoClick = () => {
    const {origin, refText} = getRefTagAndOrigin();
    handleTracking('jsClickMainLogo', {
      event_category: 'jsHomePage',
      Source: `${origin}`,
      Device: sessionStorage.getItem('device') || 'desktop',
      Ref: `${refText}`,
    });
    handleUbaTracking('jsHomePageClick', 'hiristLogo');
    window.location.href = '/';
  };

  const onAuthSuccess = () => {
    setCampaignData(queryParams.ref || '', city);
    openUrl(
      `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${
        queryParams.ref === talentBridgeContants.refCode_temp
          ? `${talentBridgeContants.redirectionUrl_temp}/${city}`
          : talentBridgeContants.redirectionUrl_perm
      }?${formQueryParams(
        queryParams.ref === talentBridgeContants.refCode_temp
          ? talentBridgeContants.refCode_temp
          : talentBridgeContants.refCode_perm
      )}${
        queryParams.ref === talentBridgeContants.refCode_perm
          ? '&campaignLogin=true'
          : ''
      }`,
      '_self'
    );
  };

  const PageContentWrapper = styled('div')(({theme}) => ({
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',

    zIndex: 5,
    position: 'relative',
    top: 0,
    marginBottom: true ? 0 : 0,
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
    },
    [theme.breakpoints.up('md')]: {
      '&::before': {
        height: 56.25,
      },
    },
  }));
  // Inline styles
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const cookieBannerData = {
    policyLinks: {
      privacyPolicy: 'https://www.iimjobs.com/privacy/',
      cookiePolicy: 'https://www.iimjobs.com/cookiepolicy',
    },
    CTA: 'Got it',
  };

  return (
    <Box sx={{my: -8}}>
      <HiristAuthentication
        variant={VersionVariants.VERSION_TWO}
        openDialog={loginOpen}
        setOpenDialog={() => {
          setLoginOpen(false);
        }}
        onSuccess={onAuthSuccess}
        onSuccessFulRegister={() =>
          setCampaignData(queryParams.ref || '', city)
        }
        customRedirectionUrl={
          queryParams.ref === talentBridgeContants.refCode_temp
            ? `/${talentBridgeContants.redirectionUrl_temp}/${city}?ref=${talentBridgeContants.refCode_temp}`
            : ''
        }
      />
      <PageContentWrapper>
        <Img src={BgCurve as unknown as string} alt="background image" />

        <Box
          sx={{
            padding: {md: '12px 70px 0px 70px', xxs: '16px 70px 0px 25px'},
            display: 'flex',
            justifyContent: {xxs: 'flex-start', md: 'space-between'},
          }}
        >
          <a
            href={`/`}
            style={{
              textDecoration: 'none',
            }}
          >
            <Image
              src="https://hirist-assets.s3.ap-south-1.amazonaws.com/hirist-talent-bridge.webp"
              alt="HIRIST.TECH"
              className="sc-kEYyzF iPqUTn"
              height={79}
              // width={155}
              onClick={handleLogoClick}
              style={{
                cursor: 'pointer',
                marginTop: 1.25,
                marginBottom: -1.125,
              }}
            />
          </a>
        </Box>
        <TalentBridgeBanner
          city={city}
          setLoginPopUp={setLoginOpen}
          driveBannerObj={driveBannerObj}
          redirectionUrl={
            queryParams.ref === talentBridgeContants.refCode_temp
              ? `${talentBridgeContants.redirectionUrl_temp}/${city}`
              : talentBridgeContants.redirectionUrl_perm
          }
          refCode={
            queryParams.ref === talentBridgeContants.refCode_temp
              ? talentBridgeContants.refCode_temp
              : talentBridgeContants.refCode_perm
          }
          customParam={
            queryParams.ref === talentBridgeContants.refCode_perm
              ? '&campaignLogin=true'
              : ''
          }
        />
        <HomePageCompanyIcons
          isTechDrive={true}
          companyLogos={driveBannerObj?.companyLogos}
        />
        <Static isTechDrive={true} setLoginPopUp={setLoginOpen} blogs={[]} />
        <HomePageFooter />
      </PageContentWrapper>
      <CookieBanner
        showCookieBanner={showCookieBanner}
        setShowCookieBanner={setShowCookieBanner}
        policyLinks={cookieBannerData.policyLinks}
        CTA={cookieBannerData.CTA}
      />
    </Box>
  );
};

export default TalentBridgeLanding;
